// Entrada pública de Modo Auditor (2026-09-09) — dos caminos al mismo
// destino (/auditoria/checklist): un QR cuyo enlace trae un código de UN SOLO USO
// en el fragmento (#e=..., BA-C-07: el token de sesión ya no viaja en la URL),
// o un PIN de 8 dígitos tecleado a mano cuando no se puede escanear. (Los
// enlaces anteriores con ?token=... siguen entrando hasta que vencen, 4 h.) Nunca pide usuario/contraseña — el inspector no tiene cuenta en
// el sistema, a propósito (ver artifact "Modo Auditor").
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTenantActual } from '@/auth/useTenantActual';
import { guardarSesionAuditor } from '@/auth/auditorSession';
import { canjearEnlaceAuditoria, canjearPinAuditoria } from '@/lib/hceAuditoria';

/** Código de un solo uso del fragmento de la URL (`#e=<código>`), o null. */
function codigoDeEnlace(): string | null {
  return new URLSearchParams(window.location.hash.replace(/^#/, '')).get('e');
}

const LARGO_PIN = 8;
/** Compatibilidad con las visitas anteriores a BA-C-07 (PIN de 6 dígitos, vigentes hasta 4 h tras el despliegue): quitar cuando ya no quede ninguna. */
const LARGO_PIN_ANTERIOR = 6;
/** El botón se habilita solo con un PIN completo: 8 dígitos (los 6-7 de un PIN nuevo contarían como fallo y sumarían al bloqueo); 6 solo por compatibilidad. */
export const pinCompleto = (pin: string): boolean => pin.length === LARGO_PIN || pin.length === LARGO_PIN_ANTERIOR;

export function AccesoAuditoriaPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantActual = useTenantActual();
  const [pin, setPin] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codigo] = useState<string | null>(() => codigoDeEnlace());
  const canjeIniciado = useRef(false); // el código es de un solo uso: ni un doble efecto (StrictMode) ni un re-render lo canjean dos veces

  useEffect(() => {
    // Enlace anterior al cambio (token en la consulta): sigue valiendo hasta que vence.
    const token = searchParams.get('token');
    if (!token) return;
    if (guardarSesionAuditor(token)) void navigate('/auditoria/checklist', { replace: true });
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!codigo || tenantActual.estado !== 'ok' || canjeIniciado.current) return;
    canjeIniciado.current = true;
    // El código sale de la URL de inmediato (no queda en el historial) y se canjea UNA vez por la sesión.
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    canjearEnlaceAuditoria(tenantActual.tenant.subdominio, codigo)
      .then((resultado) => {
        // Si la sesión no se pudo guardar, NO se navega (rebotaría sin explicación): el código ya se consumió, hay que pedir un acceso nuevo.
        if (!guardarSesionAuditor(resultado.token)) {
          setError(t('auditoria.errorSesion'));
          return;
        }
        void navigate('/auditoria/checklist', { replace: true });
      })
      .catch((err: unknown) => {
        const base = err instanceof Error ? err.message : t('auditoria.errorPin');
        // Solo si el servidor garantiza que el código NO se consumió (429) tiene sentido pedir que vuelva a abrir el enlace; en otro caso no se promete nada.
        const noConsumido = typeof err === 'object' && err !== null && (err as { noConsumido?: boolean }).noConsumido === true;
        setError(noConsumido ? `${base} ${t('auditoria.reabrirEnlace')}` : base);
      });
  }, [codigo, tenantActual, navigate, t]);

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (tenantActual.estado !== 'ok') return;
    setError(null);
    setEnviando(true);
    try {
      const resultado = await canjearPinAuditoria(tenantActual.tenant.subdominio, pin.trim());
      if (!guardarSesionAuditor(resultado.token)) {
        setError(t('auditoria.errorSesion'));
        return;
      }
      void navigate('/auditoria/checklist', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auditoria.errorPin'));
    } finally {
      setEnviando(false);
    }
  }

  if (searchParams.get('token') || (codigo && !error && tenantActual.estado !== 'no-encontrado')) {
    return <div className="min-h-screen bg-hce-paper" />; // redirige en el efecto de arriba, sin parpadeo de formulario.
  }

  return (
    <div className="min-h-screen bg-hce-paper flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm bg-hce-surface border border-border rounded-lg shadow-sm p-6">
        <div className="text-center mb-6">
          {tenantActual.estado === 'ok' && <div className="text-sm font-medium text-hce-ink">{tenantActual.tenant.clinicName}</div>}
          {tenantActual.estado === 'no-encontrado' && (
            <Alert variant="red" className="text-left">
              <AlertDescription>{t('auth.clinicaNoEncontrada')}</AlertDescription>
            </Alert>
          )}
        </div>

        {tenantActual.estado === 'ok' && (
          <>
            <h1 className="text-base font-medium text-hce-ink mb-1">{t('auditoria.accesoTitulo')}</h1>
            <p className="text-sm text-hce-muted mb-4">{t('auditoria.accesoDescripcion')}</p>

            <form onSubmit={(e) => void enviar(e)} className="space-y-3">
              {error && (
                <Alert variant="red">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">{t('auditoria.pin')}</label>
                <Input
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder={t('auditoria.pinPlaceholder')}
                  inputMode="numeric"
                  className="text-center text-2xl font-mono tracking-widest"
                  maxLength={8}
                  required
                />
              </div>
              <Button type="submit" disabled={enviando || !pinCompleto(pin)} className="w-full">
                {enviando ? t('auditoria.entrando') : t('auditoria.entrar')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
